import React from 'react';
import Icon from './Icon';

/**
 * BlocPrixVente — Section VENTE partagee dans les pages Production*.
 *
 * Props :
 *   mode              — 'kg' | 'unite'
 *   prixKg            — valeur champ prix/kg (string ou number)
 *   prixUnite         — valeur champ prix/unite (string ou number)
 *   onModeChange      — (m) => void
 *   onPrixKgChange    — (v) => void
 *   onPrixUniteChange — (v) => void
 *   hasToggle         — bool (false pour Porc/Saucisses/Sauce — affiche seulement prix/unite)
 *   verrouille        — bool (badge verrou + disabled si true)
 *   poidsTotal        — number | null (poids total en grammes pour calcul live en mode kg)
 *   labelUnite        — string (ex: "Prix de vente a l'unite ($/paquet)", defaut generique)
 */
export default function BlocPrixVente({
  mode = 'unite',
  prixKg = '',
  prixUnite = '',
  onModeChange,
  onPrixKgChange,
  onPrixUniteChange,
  hasToggle = true,
  verrouille = false,
  poidsTotal = null,
  labelUnite = "Prix de vente a l'unite ($)",
}) {
  const prixKgNum    = parseFloat(prixKg) || 0;
  const poidsTotalKg = poidsTotal != null ? poidsTotal / 1000 : null;
  const totalLive    = (hasToggle && mode === 'kg' && poidsTotalKg != null && prixKgNum > 0)
    ? Math.round(poidsTotalKg * prixKgNum * 100) / 100
    : null;

  return (
    <div style={{
      background: 'color-mix(in srgb, var(--accent) 4%, transparent)',
      border: '1px solid color-mix(in srgb, var(--accent) 18%, transparent)',
      borderRadius: 'var(--radius-md)',
      padding: 'var(--space-4)',
      marginTop: 'var(--space-3)',
    }}>
      {/* Titre */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <Icon name="argent" size={14} color="var(--accent)" />
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>
          Prix de vente au detail
        </span>
        {verrouille && (
          <span
            className="badge badge--warning"
            title="Finalise — non modifiable (impact comptable/stock)"
            style={{ marginLeft: 4 }}
          >
            <Icon name="verrou" size={11} /> Verrouille
          </span>
        )}
      </div>

      {/* Toggle $/kg <-> $/unite */}
      {hasToggle && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            className={`btn btn--sm ${mode === 'unite' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => !verrouille && onModeChange('unite')}
            disabled={verrouille}
          >
            $/unite fixe
          </button>
          <button
            type="button"
            className={`btn btn--sm ${mode === 'kg' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => !verrouille && onModeChange('kg')}
            disabled={verrouille}
          >
            $/kilogramme
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Champ $/kg */}
        {hasToggle && (
          <div className="field" style={{ flex: '0 0 180px' }}>
            <label>
              Prix de vente ($/kg)
              {mode !== 'kg' && <span className="muted" style={{ marginLeft: 4, fontWeight: 400 }}>(inactif)</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={prixKg}
              onChange={e => onPrixKgChange && onPrixKgChange(e.target.value)}
              disabled={verrouille || mode !== 'kg'}
              placeholder="ex: 35.00"
              style={{ opacity: (verrouille || mode !== 'kg') ? .45 : 1 }}
            />
          </div>
        )}

        {/* Champ $/unite */}
        <div className="field" style={{ flex: '0 0 200px' }}>
          <label>
            {labelUnite}
            {hasToggle && mode !== 'unite' && <span className="muted" style={{ marginLeft: 4, fontWeight: 400 }}>(inactif)</span>}
          </label>
          <input
            type="number"
            step="0.01"
            value={prixUnite}
            onChange={e => onPrixUniteChange && onPrixUniteChange(e.target.value)}
            disabled={verrouille || (hasToggle && mode !== 'unite')}
            placeholder="ex: 28.00"
            style={{ opacity: (verrouille || (hasToggle && mode !== 'unite')) ? .45 : 1 }}
          />
        </div>

        {/* Total live en mode kg */}
        {totalLive !== null && (
          <div style={{
            flex: '0 0 auto',
            background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3) var(--space-4)',
            textAlign: 'center',
            border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
          }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Total batch estime
            </div>
            <div style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--accent)',
              fontFeatureSettings: "'tnum' 1",
            }}>
              {totalLive.toFixed(2)} $
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
